import React from 'react'
import styled, { css } from 'styled-components'
import { TextInput, TextInputProps } from './TextInput'
import { Icon } from './Icon/index'
import { color } from '../styles'
import { InputContainer } from './InputContainer'
import { Select, SelectOption } from './Select'
import { SPACING } from '../styles/mixins/constants'
import { TestingKey, insertTestingSelector } from '../util/testing'

export type SearchInputProps = TextInputProps & {
    onSearch: () => void
    onSearchValue: (nextValue: string) => void
    onSelect?: (selectedValue: string) => void
    searchDraftValue?: string
    selectOptions?: SelectOption[]
    selectValue?: string | null
    selectPlaceholder?: string
    InputElement?: JSX.Element
    testingKeys?: [TestingKey] | [TestingKey, string]
}

export const SearchInput = ({
    label,
    errorMessage,
    hasDanger: displayDanger,
    disabled,
    selectOptions,
    selectValue,
    searchDraftValue,
    selectPlaceholder,
    InputElement,
    onSelect,
    onSearch,
    onSearchValue,
    className,
    testingKeys,
    ...props
}: SearchInputProps): JSX.Element => {
    const inputSelector = testingKeys?.length ? insertTestingSelector(testingKeys[0], 'Input') : {}
    const buttonSelector = testingKeys?.length
        ? insertTestingSelector(testingKeys[0], 'Button')
        : {}
    const addonBefore = selectOptions ? (
        <SelectContainer>
            <Select
                disableBorder
                options={selectOptions}
                value={selectValue}
                onChange={(nextOption) => {
                    if (!nextOption) return
                    onSelect?.(nextOption)
                }}
                noPadding
                squareRightBorder
                placeholder={selectPlaceholder}
                disableClear
                accented
                disableSearch
                menuWidthPx={200}
                backgroundColor="muted"
                testingKeys={testingKeys}
            />
        </SelectContainer>
    ) : undefined
    const addonAfter = (
        <MagnifyingGlass
            size={4}
            color="secondary"
            customSize
            disabled={disabled}
            onClick={onSearch}
            testingSelectors={testingKeys?.length ? [testingKeys[0], 'Button'] : undefined}
        />
    )

    const PassedInput = InputElement ? (
        <PassedInputContainer>
            {addonBefore}
            <PassedFieldContainer {...inputSelector}>{InputElement}</PassedFieldContainer>
            {addonAfter}
        </PassedInputContainer>
    ) : null

    return (
        <Container
            className={className}
            errorMessage={errorMessage}
            disabled={Boolean(disabled)}
            hasDanger={!!errorMessage || !!displayDanger}
            testingKey={testingKeys?.[0]}
            label={label}
        >
            <Content hasRightOptions={Boolean(selectOptions)}>
                {PassedInput || (
                    <StyledTextInput
                        {...props}
                        testingKeys={testingKeys?.[0] ? [testingKeys[0], 'Input'] : undefined}
                        addonBefore={addonBefore}
                        addonAfter={addonAfter}
                        hasDanger={Boolean(errorMessage || displayDanger)}
                        disabled={disabled}
                        onChange={(value) => onSearchValue(value)}
                        value={searchDraftValue}
                    />
                )}
            </Content>
        </Container>
    )
}

const Container = styled(InputContainer)`
    display: flex;
    flex-flow: column nowrap;
    min-height: 0px;
    flex: 1 1 auto;
`

const StyledTextInput = styled(TextInput)``

const Content = styled.div<{ hasRightOptions: boolean }>`
    display: flex;
    flex-flow: row nowrap;
    position: relative;
    flex: 1 1 auto;
    min-height: 0px;
`

const MagnifyingGlass = styled(Icon).attrs({
    iconKey: 'Search',
})`
    && {
        position: relative;
        cursor: pointer;
        padding: ${SPACING[2]};
        ${(p: { disabled?: boolean }) =>
            p.disabled
                ? css`
                      cursor: not-allowed;
                  `
                : css`
                      &:hover {
                          border-color: ${color.brand.primary};
                      }
                      &:active {
                          opacity: 0.5;
                      }
                  `}
    }
`

const SelectContainer = styled.div`
    position: relative;
    cursor: pointer;
    width: 175px;
`

const PassedInputContainer = styled.div`
    display: flex;
    flex-flow: row nowrap;
    border: 1px solid ${color.border.primary};
    background-color: ${color.background.muted};
    flex: 1 1 auto;
`

const PassedFieldContainer = styled.div`
    display: flex;
    flex: 1 1 auto;
`

SearchInput.Select = SelectContainer
